/**
 * Enqueuing a job of a REGISTERED type — the call sites' entry point.
 *
 * `enqueueJob` in the store takes a `dedupeKey` and a `lockKey` as plain strings, because the store must not
 * depend on the registry. That is right for the store and wrong for a call site: it means every caller
 * restates the two keys, and a restated key is a key that drifts from the definition. The consequences are
 * asymmetric and both bad — a stale `dedupeKey` silently creates duplicate work, a stale `lockKey` silently
 * removes the exclusion that made it safe.
 *
 * So callers name a TYPE and a payload, and the keys come from that type's own definition. Exactly what
 * `reconcileExam` did before it was deleted, which is why it is expressed here once rather than at each call site.
 */

import { logger } from '@adornis/base/logging.js';
import { enqueueJob } from './job-store.js';
import type { JobDefinition, JobDoc, JobPayload } from './job.js';
import { getJobDefinition } from './registry.js';

export const dedupeKeyFor = (definition: JobDefinition, examId: string, payload: JobPayload): string =>
  definition.dedupeKey?.(examId, payload) ?? `${definition.type}:${examId}`;

/** The type's own lock granularity; `enqueueJob` falls back to the fully serial `exam:<id>`. */
export const lockKeyFor = (definition: JobDefinition, examId: string, payload: JobPayload): string | undefined =>
  definition.lockKey?.(examId, payload);

/**
 * ONE OPTION, because one is all any call site ever passed.
 *
 * `priority`, `maxWaitMs` and `runAfter` were here too, and `reconcile.ts` was their only producer — so removing
 * that file made all three provably dead, along with the runner's `maxWaitMs` cancellation branch and the
 * `priority` key in `job_exam_due_scan`. Add one back when something needs it, not before.
 */
export interface EnqueueOptions {
  /** This row's runtime budget, for work whose size is known at enqueue time. */
  maxRuntimeMs?: number;
  /** Which run this row belongs to — supplied by `dispatchJobs`, see `JobDoc.batchKey`. */
  batchKey?: string;
}

/**
 * Enqueues one job of a registered type, deriving its keys from the definition.
 *
 * Returns `null` when the type is unknown or the job was deduped. Both are normal: an unknown type means this
 * instance has not imported that definition (a rolling deploy), and a dedupe means the work is already
 * queued. Neither is worth throwing over at a call site whose real job is something else.
 */
export async function enqueueForType<P extends JobPayload = JobPayload>(
  type: string,
  examId: string,
  payload: P,
  options: EnqueueOptions = {},
): Promise<JobDoc<P> | null> {
  const definition = getJobDefinition<P>(type);
  if (!definition) {
    logger.error({ type, examId }, 'enqueueForType: unknown job type — nothing enqueued');
    return null;
  }

  const lockKey = lockKeyFor(definition, examId, payload);
  // NOTHING ABOUT A RUN IS PASSED HERE. A row used to carry a `batchKey` naming the pass it belonged to, which
  // every dispatcher had to open (a query), thread through (this option), and get the ORDER right on — enqueue
  // first and the row joined a pass whose members were all long since terminal, so the run read as finished
  // before the work started. Completion is now asked of the queue at a row's terminal transition, so a caller
  // that only wants work done has nothing to remember.
  return enqueueJob<P>({
    examId,
    type,
    payload,
    dedupeKey: dedupeKeyFor(definition, examId, payload),
    ...(options.batchKey ? { batchKey: options.batchKey } : {}),
    ...(lockKey ? { lockKey } : {}),
    ...(definition.maxAttempts ? { maxAttempts: definition.maxAttempts } : {}),
    ...options,
  });
}
